import { useEffect, useRef, useState } from 'react';
import { UiModeSwitch } from "@/components/UiModeSwitch";
import { NotificationSettings } from "@/components/engagement/NotificationSettings";
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useClerk } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { Bell, Copy, Check, Key, LockKey, UserCircle, ChartLineUp, Gift, UsersThree, Prohibit } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BrokerApiKeyDialog } from '@/components/BrokerApiKeyDialog';
import { BlockedUsers } from '@/components/community/BlockedUsers';
import { LanguagePicker } from '@/components/layout/LanguagePicker';
import { TierIcon } from '@/components/ui/tier-icon';
import { useAuth } from '@/contexts/AuthContext';
import { profileApi } from '@/api/client';
import { brokerCredentialsService } from '@/services/brokerCredentials';
import { isTier, tierNameKey, type Tier } from '@/lib/tiers';
import { format, fmtPct, fmtUsd, withSign } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CLERK_WINDOW_VARIABLES } from '@/lib/clerkTheme';

/** Open the Profile pop-up from anywhere (the account menu, a link). */
export const PROFILE_EVENT = 'copyfi:profile';
export const openProfile = () => window.dispatchEvent(new Event(PROFILE_EVENT));

/****************
 * THE PROFILE. Guillaume, 2026-09-26: "a pop-up just like the Trade Page and
 * the Comparator ... some stats about him, a change password button, the keys
 * he registered, etc.. and a referral link he can copy and send", and "as
 * much info as possible to build them a proper profile".
 *
 * Passwords never pass through us: "Change password" opens Clerk's own
 * account screen. Keys are listed read-only here; managing them is the same
 * dialog as on Copy Trade.
 ****************/
function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border/60 p-4 space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold">{icon}{title}</h3>
      {children}
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className="rounded-lg bg-white/[0.03] px-3 py-2">
      <p className={cn('text-base font-semibold', tone)}>{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

/****************
 * DELETE ACCOUNT: only a way to the page. Guillaume, 2026-10-11: deletion must
 * not be one tap away for someone holding an unlocked computer or phone. It
 * happens at /delete-account (pages/legal/DeleteAccount.tsx), with a reason and
 * a code emailed to the account. The phone's Profile has the same line and
 * opens the same page (mobile-app/src/app/profile.tsx).
 ****************/
function DeleteAccountLink({ onGo }: { onGo: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="space-y-1 pt-1">
      <p className="text-[11px] text-muted-foreground">{t('profile.deleteHint')}</p>
      <Button asChild variant="ghost" size="sm" className="text-danger hover:text-danger px-0">
        <Link to="/delete-account" onClick={onGo}>{t('profile.deleteAccount')}</Link>
      </Button>
    </div>
  );
}

export function ProfileDialog() {
  const { t } = useTranslation('common');
  const { user, isRealAdmin } = useAuth();
  const clerk = useClerk();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  // "Change password" pressed: Clerk's window opens once this pop-up has closed (onCloseAutoFocus).
  const clerkNextRef = useRef(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(PROFILE_EVENT, onOpen);
    return () => window.removeEventListener(PROFILE_EVENT, onOpen);
  }, []);

  const profile = useQuery({ queryKey: ['profile', user?.id], queryFn: profileApi.get, enabled: open && !!user?.id });
  const keys = useQuery({
    queryKey: ['profile-keys', user?.id],
    queryFn: () => brokerCredentialsService.getAllCredentials(),
    enabled: open && !!user?.id,
  });

  const p = profile.data;
  // Admins are Admin (Guillaume, 2026-09-27: "My account is admin but listed as
  // Free"). The server has no admin concept until B-20; the web app's list does.
  const tier: Tier = isRealAdmin ? 'admin' : p && isTier(p.tier) ? p.tier : 'free';
  const s = p?.stats;
  const days = p?.signed_up ? Math.max(0, Math.floor((Date.now() - new Date(p.signed_up).getTime()) / 86_400_000)) : null;
  const copy = (what: 'link' | 'code', text: string) => {
    void navigator.clipboard?.writeText(text).then(() => { setCopied(what); setTimeout(() => setCopied(null), 1500); });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-y-auto"
        onCloseAutoFocus={(e) => {
          if (!clerkNextRef.current) return;
          clerkNextRef.current = false;
          // The focus goes to Clerk's window, not back to the account menu.
          e.preventDefault();
          clerk.openUserProfile({
            // Our colours, and without Clerk's own "API keys" page: those are
            // developer keys for Clerk, not exchange keys, and it confused
            // (Guillaume, 2026-09-27: "it open the clerk and it's wierd").
            appearance: { variables: CLERK_WINDOW_VARIABLES },
            apiKeysProps: { hide: true },
          });
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('profile.title')}</DialogTitle>
        </DialogHeader>

        {profile.isLoading && <p className="text-sm text-muted-foreground">{t('profile.loading')}</p>}
        {profile.isError && <p className="text-sm text-danger">{t('profile.failed')}</p>}

        {p && (
          <div className="grid gap-3 md:grid-cols-2">
            {/* ── Account ─────────────────────────────────────────── */}
            <Section icon={<UserCircle size={16} />} title={t('profile.account')}>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[13px]">
                <dt className="text-muted-foreground">{t('profile.email')}</dt>
                <dd className="truncate">{user?.email ?? '—'}</dd>
                {p.handle && (<><dt className="text-muted-foreground">{t('profile.handle')}</dt><dd>@{p.handle}</dd></>)}
                <dt className="text-muted-foreground">{t('profile.plan')}</dt>
                <dd className="inline-flex items-center gap-1.5"><TierIcon tier={tier} size={13} />{t(tierNameKey(tier))}</dd>
                <dt className="text-muted-foreground">{t('profile.memberSince')}</dt>
                <dd>{p.signed_up ? format(new Date(p.signed_up), 'MMM d, yyyy') : '—'}</dd>
                <dt className="text-muted-foreground">{t('profile.language')}</dt>
                <dd><LanguagePicker compact /></dd>
              </dl>
              {/* Clerk's window opens after this pop-up closes. Opened over it, the
                  pop-up kept the focus and the page's clicks to itself until a click
                  outside closed it (2026-10-31). */}
              <Button variant="outline" size="sm" className="gap-2" onClick={() => { clerkNextRef.current = true; setOpen(false); }}>
                <LockKey size={14} />{t('profile.changePassword')}
              </Button>
              <DeleteAccountLink onGo={() => setOpen(false)} />
            </Section>

            {/* ── Stats ───────────────────────────────────────────── */}
            <Section icon={<ChartLineUp size={16} />} title={t('profile.stats')}>
              {p.stats_error && <p className="text-[12px] text-danger">{t('profile.statsFailed')}</p>}
              {s && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Stat label={t('profile.sessions')} value={s.sessions} />
                  <Stat label={t('profile.running')} value={s.running} />
                  <Stat label={t('profile.byKind')} value={`${s.live_sessions} · ${s.paper_sessions} · ${s.signal_sessions}`} />
                  <Stat label={t('profile.closedTrades')} value={s.closed_trades} />
                  <Stat label={t('profile.winRate')} value={s.closed_trades ? fmtPct(Math.round((s.wins / s.closed_trades) * 100), 0) : '—'} />
                  <Stat
                    label={t('profile.netPnl')}
                    value={withSign(s.net_pnl_usd > 0, fmtUsd(s.net_pnl_usd, 2))}
                    tone={s.net_pnl_usd > 0 ? 'text-success' : s.net_pnl_usd < 0 ? 'text-danger' : undefined}
                  />
                  <Stat
                    label={t('profile.liveNetPnl')}
                    value={withSign(s.live_net_pnl_usd > 0, fmtUsd(s.live_net_pnl_usd, 2))}
                    tone={s.live_net_pnl_usd > 0 ? 'text-success' : s.live_net_pnl_usd < 0 ? 'text-danger' : undefined}
                  />
                  <Stat label={t('profile.daysOn')} value={days ?? '—'} />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">{t('profile.byKindHint')}</p>
            </Section>

            {/* ── Notifications and sounds (2026-10-21) ─────────────── */}
            <Section icon={<Bell size={16} />} title={t('notif.title')}>
              <UiModeSwitch />
              <NotificationSettings />
            </Section>

            {/* ── API keys ────────────────────────────────────────── */}
            <Section icon={<Key size={16} />} title={t('profile.keys')}>
              {keys.isLoading && <p className="text-[12px] text-muted-foreground">{t('profile.loading')}</p>}
              {keys.isError && <p className="text-[12px] text-danger">{t('profile.keysFailed')}</p>}
              {keys.data && keys.data.length === 0 && <p className="text-[12px] text-muted-foreground">{t('profile.noKeys')}</p>}
              <div className="space-y-1.5">
                {(keys.data ?? []).map((k) => (
                  <div key={k.id} className="flex items-center gap-2 rounded-lg bg-white/[0.03] px-3 py-2 text-[13px]">
                    <span className="flex-1 truncate">{k.label || k.exchange_name}</span>
                    <span className="text-[11px] text-muted-foreground capitalize">{k.exchange_name}</span>
                    <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', k.is_testnet ? 'bg-primary/15 text-primary' : 'bg-success/15 text-success')}>
                      {k.is_testnet ? t('profile.keyTest') : t('profile.keyLive')}
                    </span>
                  </div>
                ))}
              </div>
              <BrokerApiKeyDialog>
                <Button variant="outline" size="sm" className="gap-2"><Key size={14} />{t('profile.manageKeys')}</Button>
              </BrokerApiKeyDialog>
            </Section>

            {/* ── Referral ────────────────────────────────────────── */}
            <Section icon={<Gift size={16} />} title={t('profile.referral')}>
              <p className="text-[12px] text-muted-foreground">{t('profile.referralHint')}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate rounded-lg border border-border/60 px-3 py-2 text-[12px]">{p.referral.link}</code>
                <Button size="sm" className="gap-1.5 shrink-0" onClick={() => copy('link', p.referral.link)}>
                  {copied === 'link' ? <Check size={14} /> : <Copy size={14} />}{t('profile.copyLink')}
                </Button>
              </div>
              <p className="text-[12px]">
                {t('profile.yourCode')}{' '}
                <button type="button" className="font-mono font-semibold hover:underline" onClick={() => copy('code', p.referral.code)}>
                  {p.referral.code}
                </button>
                {copied === 'code' && <Check size={12} className="inline ms-1 text-success" />}
              </p>
              <p className="text-[12px] text-muted-foreground">
                {p.referral.referred_by
                  ? t('profile.referredBy', { name: p.referral.referred_by.name || t('profile.aMember') })
                  : t('profile.referredByNone')}
              </p>
            </Section>

            {/* ── People they brought ─────────────────────────────── */}
            <div className="md:col-span-2">
              <Section icon={<UsersThree size={16} />} title={t('profile.people', { count: p.referral.people.length })}>
                {p.referral.people.length === 0 && <p className="text-[12px] text-muted-foreground">{t('profile.peopleNone')}</p>}
                <div className="divide-y divide-border/40">
                  {p.referral.people.map((x, i) => {
                    const xt: Tier = isTier(x.tier) ? x.tier : 'free';
                    return (
                      <div key={i} className="flex items-center gap-3 py-2 text-[13px]">
                        <span className="flex-1 truncate">{x.name || t('profile.aMember')}</span>
                        <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground"><TierIcon tier={xt} size={12} />{t(tierNameKey(xt))}</span>
                        <span className="text-[12px] text-muted-foreground w-24 text-end">{format(new Date(x.signed_up || x.since), 'MMM d, yyyy')}</span>
                      </div>
                    );
                  })}
                </div>
              </Section>
            </div>

            {/* ── Blocked users (Community) ───────────────────────── */}
            <div className="md:col-span-2">
              <Section icon={<Prohibit size={16} />} title={t('community:blocked.title')}>
                <BlockedUsers enabled={open} />
              </Section>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
